import type {
  BunkerGameSettings,
  BunkerRoomSummary,
  AliasGameSettings,
  AliasRoomSummary,
  AliasTeamSeed,
  GameSettings,
  LobbyView,
  QuizBattlePack,
  QuizBattleRoomSummary,
  QuizBattleSettings,
  TeamSummary,
  Team,
  ZayacGameSettings,
  ZayacLobbyView,
  ZayacRoomSummary,
} from '@shum/shared';
import { API_BASE } from './runtime';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = (data as { message?: string }).message ?? `Ошибка ${res.status}`;
    throw new Error(message);
  }
  return data as T;
}

export interface CreateRoomResponse {
  roomId: string;
  code: string;
  joinUrl: string;
  qrPayload: string;
}

export type CreateBunkerRoomResponse = BunkerRoomSummary;

export const api = {
  listTeams: () => req<TeamSummary[]>('/teams').then((t) => t),
  getTeam: (id: string) => req<Team>(`/teams/${id}`),
  createTeam: (name: string, players: string[]) =>
    req<Team>('/teams', { method: 'POST', body: JSON.stringify({ name, players }) }),
  updateTeam: (id: string, body: { name?: string; players?: string[] }) =>
    req<Team>(`/teams/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTeam: (id: string) => req<{ ok: boolean }>(`/teams/${id}`, { method: 'DELETE' }),

  createRoom: (body: { players: string[]; creatorName?: string | null; settings?: Partial<GameSettings> }) =>
    req<CreateRoomResponse>('/rooms', { method: 'POST', body: JSON.stringify(body) }),
  getRoom: (code: string) => req<LobbyView & { phase: string; dayNumber: number; winner: string | null }>(`/rooms/${code}`),
  updateRoom: (code: string, body: { players?: string[]; settings?: Partial<GameSettings>; presetId?: string }) =>
    req<LobbyView>(`/rooms/${code}`, { method: 'PATCH', body: JSON.stringify(body) }),
  startRoom: (code: string) => req<{ ok: boolean; gameId: string }>(`/rooms/${code}/start`, { method: 'POST' }),
  roomHistory: (code: string) =>
    req<{ games: { gameId: string; roomId: string; startedAt: string; finishedAt: string | null; winner: string | null; playerCount: number }[] }>(
      `/rooms/${code}/history`,
    ),

  createZayacRoom: (body: { players: string[]; creatorName?: string | null; settings?: Partial<ZayacGameSettings> }) =>
    req<CreateRoomResponse>('/zayac/rooms', { method: 'POST', body: JSON.stringify(body) }),
  getZayacRoom: (code: string) => req<ZayacLobbyView & ZayacRoomSummary>(`/zayac/rooms/${code}`),
  updateZayacRoom: (code: string, body: { players?: string[]; settings?: Partial<ZayacGameSettings> }) =>
    req<ZayacLobbyView>(`/zayac/rooms/${code}`, { method: 'PATCH', body: JSON.stringify(body) }),
  startZayacRoom: (code: string) => req<{ ok: boolean; gameId: string }>(`/zayac/rooms/${code}/start`, { method: 'POST' }),
  zayacRoomHistory: (code: string) =>
    req<{ games: { gameId: string; roomId: string; startedAt: string; finishedAt: string | null; winnerPlayerId: string | null; playerCount: number; rounds: number }[] }>(
      `/zayac/rooms/${code}/history`,
    ),

  createBunkerRoom: (body: { code?: string; players: string[]; creatorName?: string | null; settings?: Partial<BunkerGameSettings> }) =>
    req<CreateBunkerRoomResponse>('/bunker/rooms', { method: 'POST', body: JSON.stringify(body) }),
  getBunkerRoom: (code: string) => req<BunkerRoomSummary & { players: import('@shum/shared').BunkerPlayerPublic[] }>(`/bunker/rooms/${code}`),

  createAliasRoom: (body: { code?: string; players: string[]; creatorName?: string | null; teams: AliasTeamSeed[]; settings?: Partial<AliasGameSettings> }) =>
    req<AliasRoomSummary>('/alias/rooms', { method: 'POST', body: JSON.stringify(body) }),
  getAliasRoom: (code: string) => req<AliasRoomSummary>(`/alias/rooms/${code}`),

  createQuizRoom: (body: { code?: string; pack: QuizBattlePack; settings?: Partial<QuizBattleSettings> }) =>
    req<CreateRoomResponse>('/quiz/rooms', { method: 'POST', body: JSON.stringify(body) }),
  getQuizRoom: (code: string) => req<QuizBattleRoomSummary>(`/quiz/rooms/${code}`),
};
